import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { api } from "../lib/api";

/** Bell icon with unread badge + dropdown feed of in-app notifications. */
export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count", { withCredentials: true });
      setUnread(r.data?.count || 0);
    } catch (_e) { /* silent — user might not be logged in */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { withCredentials: true });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (_e) { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60_000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  useEffect(() => {
    const onClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    setItems((cur) => cur.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`, {}, { withCredentials: true }); } catch (_e) { /* silent */ }
  };

  const markAll = async () => {
    setItems((cur) => cur.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    try { await api.post("/notifications/read-all", {}, { withCredentials: true }); } catch (_e) { /* silent */ }
  };

  const del = async (id) => {
    const was = items.find((n) => n.id === id);
    setItems((cur) => cur.filter((n) => n.id !== id));
    if (was && !was.read_at) setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/notifications/${id}`, { withCredentials: true }); } catch (_e) { /* silent */ }
  };

  const openNotif = (notif) => {
    if (!notif.read_at) markRead(notif.id);
    setOpen(false);
    if (notif.cta_url) navigate(notif.cta_url);
  };

  return (
    <div ref={ref} style={{ position: "relative" }} data-testid="notifications-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell-button"
        aria-label="Notificaciones"
        style={{
          position: "relative", width: 40, height: 40, borderRadius: 999,
          background: "#fff", border: "1px solid #e5e7eb", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Bell size={18} color="#374151" />
        {unread > 0 && (
          <span
            data-testid="notifications-bell-badge"
            style={{
              position: "absolute", top: -2, right: -2, minWidth: 18, height: 18, padding: "0 5px",
              borderRadius: 999, background: "#ef4444", color: "#fff",
              font: '800 11px "Nunito", sans-serif',
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #fff",
            }}
          >{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-dropdown"
          style={{
            position: "absolute", top: 46, right: 0, width: 360, maxHeight: 480, overflowY: "auto",
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
            boxShadow: "0 20px 40px rgba(15,23,42,.15)", zIndex: 60,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid #f1f5f9" }}>
            <b style={{ font: '800 14px "Nunito", sans-serif', color: "#0f172a" }}>Notificaciones</b>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                data-testid="notifications-mark-all"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#2563eb", font: '700 12px "Nunito", sans-serif', cursor: "pointer" }}
              >
                <CheckCheck size={14} /> Marcar todas
              </button>
            )}
          </div>
          {loading && <div style={{ padding: 20, color: "#6b7280", font: '600 13px "Nunito", sans-serif' }}>Cargando…</div>}
          {!loading && items.length === 0 && (
            <div style={{ padding: "28px 20px", textAlign: "center", color: "#6b7280", font: '600 13px "Nunito", sans-serif' }} data-testid="notifications-empty">
              🔕 No tienes alertas por ahora.
              <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>Te avisaremos cuando se acerque una fecha del SAT.</div>
            </div>
          )}
          {!loading && items.map((n) => {
            const unreadRow = !n.read_at;
            return (
              <div
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                style={{
                  padding: "12px 14px", borderBottom: "1px solid #f8fafc",
                  background: unreadRow ? "#eff6ff" : "#fff",
                  cursor: n.cta_url ? "pointer" : "default",
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}
                onClick={() => openNotif(n)}
              >
                <div style={{ width: 8, height: 8, marginTop: 6, borderRadius: 999, background: unreadRow ? "#2563eb" : "transparent" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '800 13px "Nunito", sans-serif', color: "#0f172a", marginBottom: 2 }}>{n.title}</div>
                  {n.body && <div style={{ font: '600 12px "Nunito", sans-serif', color: "#475569", lineHeight: 1.4 }}>{n.body}</div>}
                  {n.cta_label && n.cta_url && (
                    <div style={{ marginTop: 6, font: '800 12px "Nunito", sans-serif', color: "#2563eb" }}>{n.cta_label} →</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); del(n.id); }}
                  data-testid={`notification-delete-${n.id}`}
                  aria-label="Eliminar"
                  style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", padding: 4 }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
